/**
 * Pure helpers shared by the page editor hosts (SCRUM-2165, slice 1 of
 * SCRUM-2122). Each lived two or three times under PagesComponent/, so a fix to
 * the option mapping, the link-array write or the Link half-merge could land in
 * one screen and miss its twin:
 *
 *  - optionsForContentVar: HandleContent (optional), HandleContent (required) and
 *    IndexTemplateSelector (`optionsFor`);
 *  - withLinkArray: the same three (`applyLinkArray`);
 *  - linkAwareValueChange: UpdatePageView and UpdateHistoriesPages
 *    (`basicValueChange`).
 *
 * The hosts keep their own state and callbacks; only the logic lives here.
 */

/** The fields of a page_content entry the helpers read. */
export type ContentVarLike = {
  type?: string;
  complextype?: string;
  value?: unknown;
};

/** The option lists a host has loaded. A missing list means "no options". */
export type OptionSources<O> = {
  products?: O;
  categories?: O;
  subcategories?: O;
  posts?: O;
};

/**
 * The option list the entity blocks need for one variable.
 *
 * `extra` maps a scalar `type` to a list and wins over the shared mapping.
 * IndexTemplateSelector passes `{ Product: products, String: users }` (the
 * authorname select): the differences between the screens are kept, not
 * unified.
 */
export function optionsForContentVar<O>(
  contentVar: ContentVarLike,
  sources: OptionSources<O>,
  extra: Record<string, O | undefined> = {}
): O | undefined {
  if (contentVar.type === "Array") {
    switch (contentVar.complextype) {
      case "product":
        return sources.products;
      case "category":
        return sources.categories;
      case "posts":
        return sources.posts;
      case "subcategory":
        return sources.subcategories;
      default:
        return undefined;
    }
  }
  if (contentVar.type !== undefined && Object.prototype.hasOwnProperty.call(extra, contentVar.type)) {
    return extra[contentVar.type];
  }
  // No scalar Category / Subcategory / Blog / Ad: no template declares one
  // (SCRUM-2207).
  switch (contentVar.type) {
    case "Post":
      return sources.posts;
    default:
      return undefined;
  }
}

type ChildLink = { name?: string; link?: string };
type LinkRow = { link?: string; name?: string; children?: ChildLink[] };

/**
 * The stored shape of one row, as the update screens persist it: `{ link, name }`
 * exactly as before, plus `children` sanitised to `{ name, link }` when the row
 * has any, so no editor-local key (`index`) is stored (SCRUM-2171).
 */
const toStoredLinkRow = (row: LinkRow) => ({
  link: row.link,
  name: row.name,
  ...(Array.isArray(row.children) && row.children.length > 0
    ? { children: row.children.map((child) => ({ name: child.name, link: child.link })) }
    : {}),
});

/**
 * `variables` with the named Array:links variable's value replaced by the
 * edited rows, reduced to `toStoredLinkRow`. Returns a new list; the entries it
 * does not touch are the same objects.
 */
export function withLinkArray<V extends { variable?: string; value?: unknown }>(
  variables: V[],
  links: LinkRow[],
  variableName: string
): V[] {
  const rows = links.map(toStoredLinkRow);
  return variables.map((v) => (v.variable === variableName ? { ...v, value: rows } : v));
}

/**
 * The registry blocks report `(name, value)`. For a Link variable the value is
 * the whole `{ name, link }` object, while the hosts merge one half at a time,
 * so the edited half is found by comparing with the stored value ("name" when
 * neither changed) and handed to `onLinkHalf` as a synthetic input event. Every
 * other type goes to `onValue` unchanged.
 */
export function linkAwareValueChange(
  contentVar: ContentVarLike,
  onLinkHalf: (event: { target: { name: string; value: string } }, variableName: string) => void,
  onValue: (name: string, value: unknown) => void
) {
  return (name: string, value: unknown) => {
    if (contentVar.type === "Link") {
      const prev = (contentVar.value || {}) as Record<string, unknown>;
      const next = (value ?? {}) as Record<string, unknown>;
      const half = ["name", "link"].find((k) => (next[k] ?? "") !== (prev[k] ?? "")) ?? "name";
      onLinkHalf({ target: { name: half, value: (next[half] as string) ?? "" } }, name);
      return;
    }
    onValue(name, value);
  };
}
